/**
 * @typedef {import('./paths').RedirectConfig} RedirectConfig
 * @typedef {import('./paths').RouteConfig} RouteConfig
 */

/**
 * Options for filtering the list of routing config objects.
 *
 * @typedef {object} FilterCriteria
 * @property {boolean} enabled - filters for accessible redirects
 * @property {boolean} adminRoute - filters for admin redirects
 * @property {boolean} protectedRoute - filters for protected redirects
 */

/**
 * Filters the React Router route config objects according to the given criteria.
 * The result contains one or many app routes.
 *
 * @param {RouteConfig[]} configs - React Router config objects
 * @param {FilterCriteria?} criteria - filtering criteria for the routes
 * @returns {RouteConfig[]} a list of filtered Revenue Management routes
 */
export const filterRoutes = (configs, criteria = {}) => {
  return configs
    .filter((config) => config.enabled === criteria.enabled)
    .filter((config) => !config.adminRoute || criteria.adminRoute)
    .filter((config) => !config.protectedRoute || criteria.protectedRoute);
};

/**
 * Filters the React Router redirect config objects according to the given criteria.
 * The result contains a single, catch-all redirect. This works as long as the redirects
 * are mutually exclusive.
 *
 * @param {RedirectConfig[]} configs - React Router config objects
 * @param {FilterCriteria?} criteria - filtering criteria for the redirects
 * @returns {RedirectConfig[]} a list of filtered Revenue Management redirects
 */
export const filterRedirects = (configs, criteria = {}) => {
  return configs
    .filter((config) => config.enabled === criteria.enabled)
    .filter((config) => config.protectedRoute === criteria.protectedRoute)
    .filter((config) => config.adminRoute === criteria.adminRoute);
};

/**
 * Builds a list of React Router entities based on the routing config objects.
 * This class structure provides a fluent interface that makes subsequent
 * operations on the routes data easier to manage.
 */
export class RoutesBuilder {
  #components = [];
  #configs = [];

  #routingComponent;
  #routingSource;
  #routingFilter;

  /**
   * Constructs the class based on whether routes or redirect entities are desired.
   * This changes the final component type used with React Router.
   *
   * @param {JSX.Element} routingComponent - a React component used to map route configuration objects
   *   to React Router components; this component takes a route configuration object as its props
   * @param {function} routingSource - function used to retrieve the route configuration objects
   * @param {function} routingFilter - function used to filter the route configuration objects
   * @returns {RoutesBuilder}
   */
  constructor(routingComponent, routingSource, routingFilter) {
    this.#routingComponent = routingComponent;
    this.#routingSource = routingSource;
    this.#routingFilter = routingFilter;
  }

  /**
   * Configures the list of routing paths based on the provided options.
   * Options are passed directly to the corresponding `routing/paths.js` function.
   *
   * @param {object?} options - options that impact how path configuration is handled.
   *   These are passed directly to the corresponding `routing/paths.js` function
   * @returns {RoutesBuilder}
   */
  configure(options) {
    this.#configs = this.#routingSource(options);
    return this;
  }

  /**
   * Filters the list of routing paths based on the provided options.
   *
   * @param {FilterCriteria?} criteria - filtering criteria for the routes
   * @returns {RoutesBuilder}
   */
  filter(criteria) {
    this.#configs = this.#routingFilter(this.#configs, criteria);
    return this;
  }

  /**
   * Returns the current list of route configuration options transformed
   * into React components. The route config objects are used as props
   * for the React routing component.
   *
   * @returns {JSX.Element[]}
   */
  build() {
    this.#components = this.#configs.map(this.#routingComponent);
    return this.#components;
  }
}

export default RoutesBuilder;
